import { useCallback } from 'react'

import type { ApolloCache } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import {
  IntegrationsSyncRunOriginEnum,
  MarketplaceDbIntegration,
} from 'generated/resources/datatable.graphql'
import {
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunParameterFragment,
  IntegrationsSyncRunStatusChangeSubscriptionFragment,
  LastSyncRunDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { type ContextParameters } from 'modules/integrations/hooks/SyncContext'

interface UpdateLastSyncRunQueryCacheProps {
  marketplaceIntegration: MarketplaceDbIntegration
  contextParameters: ContextParameters
  origin: IntegrationsSyncRunOriginEnum
  syncRun: IntegrationsSyncRunFragment | IntegrationsSyncRunStatusChangeSubscriptionFragment
}

interface ReadLastSyncRunProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  contextParameters: ContextParameters
  origin: IntegrationsSyncRunOriginEnum
  syncEnabled: boolean
}

interface SyncRunParameter {
  key: string
  value: string
}

interface LastSyncRunQueryVariables {
  companyId: number
  marketplaceIntegrationId: number
  parameters: SyncRunParameter[]
  origin: IntegrationsSyncRunOriginEnum
}

const contextParametersToArray = (params: ContextParameters) => {
  return Object.entries(params).map(([key, value]) => ({ key, value }))
}

export const buildLastSyncRunVariables = (
  companyId: number,
  marketplaceIntegrationId: number,
  contextParameters: ContextParameters,
  origin: IntegrationsSyncRunOriginEnum
): LastSyncRunQueryVariables => {
  return {
    companyId,
    marketplaceIntegrationId: marketplaceIntegrationId,
    parameters: contextParametersToArray(contextParameters),
    origin,
  }
}

export const writeLastSyncRunQueryCache = (
  cache: ApolloCache<unknown>,
  { marketplaceIntegration, contextParameters, syncRun, origin }: UpdateLastSyncRunQueryCacheProps,
  companyId: number
) => {
  cache.writeQuery({
    query: LastSyncRunDocument,
    variables: buildLastSyncRunVariables(
      companyId,
      marketplaceIntegration.id,
      contextParameters,
      origin
    ),
    data: {
      integrations: {
        syncRunsConnection: {
          nodes: [
            {
              id: syncRun.id,
              finishedAt: syncRun.finishedAt,
              status: syncRun.status,
              parameters: buildSyncRunParameters(syncRun.parameters),
              __typename: 'IntegrationsSyncRun',
            },
          ],
          __typename: 'IntegrationsSyncRunConnection',
        },
        __typename: 'integrations',
      },
    },
  })
}

const buildSyncRunParameters = (
  parameters: SyncRunParameter[]
): IntegrationsSyncRunParameterFragment[] => {
  return parameters.map((param) => ({
    key: param.key,
    value: param.value,
    __typename: 'IntegrationsSyncRunParameters',
  }))
}

export const useUpdateLastSyncRunQueryCache = () => {
  const client = useApolloClient()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  return useCallback(
    (props: UpdateLastSyncRunQueryCacheProps) => {
      writeLastSyncRunQueryCache(client.cache, props, companyId)
    },
    [client, companyId]
  )
}

export const useReadLastSyncRun = ({
  marketplaceIntegration,
  contextParameters,
  origin,
  syncEnabled,
}: ReadLastSyncRunProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const marketplaceIntegrationId = marketplaceIntegration?.id

  return useQuery(LastSyncRunDocument, {
    variables: buildLastSyncRunVariables(
      companyId,
      marketplaceIntegrationId ?? 0,
      contextParameters,
      origin
    ),
    skip: !syncEnabled,
  })
}
